/**
 * Selectors for center pivot and device relationship data.
 *
 * These selectors combine CenterPivot entities with their linked device
 * configurations and activity data for unified display.
 */
import { createSelector } from "@reduxjs/toolkit"
import { createCachedSelector } from "re-reselect"

import {
  formatDurationMs,
  formatHoursPerRevolution,
  getLastKnownPivotHeading,
  getPivotActivityMetrics,
  getSectorDrawingValuesForPivotActivity,
  getTimeToNextStopMs,
  hoursPerRevolutionToSpeedPercent,
  isActivityFinished,
  isPivotClockwise,
  isPivotMoving,
  type PivotActivityMetrics,
  type PivotActivityStartAndEndValues,
} from "@/base/device-activity-utils"
import { hasSpeedByDepthConfig as checkHasSpeedByDepthConfig } from "@/base/pivot-depth-calculations"
import * as Geo from "@/base/geo"
import {
  derivePivotWetStatus,
  type PivotWetStatus,
} from "@/base/pivot-wet-status"
import type { DeviceLastKnownLocation } from "@/redux/models"
import {
  formatPivotHeading,
  getFormattedPivotMetrics,
  type FormattedPivotMetrics,
} from "@/base/pivot-formatters"
import type { CenterPivot } from "@/redux/center-pivots.reducer"
import { selectAllCenterPivots, selectCenterPivotById } from "@/redux/center-pivots.reducer"
import type { DeviceConfiguration } from "@/redux/device-configurations.reducer"
import type { DeviceStatusData } from "@/redux/device-event-last.reducer"
import { getDeviceStatusListItemsSortedFromState, getSensorEventLastsForDevice } from "@/redux/device-event-last.reducer"
import type { DeviceActivity } from "@/redux/models"
import * as Models from "@/redux/models"
import type { RootState } from "@/redux/root.reducer"

/**
 * Combined status for a center pivot, merging controller commands and monitor sensor data.
 */
export interface CombinedPivotStatus {
  // Sensor readings (from monitor activity)
  currentHeadingDegrees: number | null,
  direction: "clockwise" | "counterclockwise" | null

  // Capabilities based on linked devices
  hasController: boolean,
  hasMonitor: boolean,

  isEndGunOn: boolean,
  // Control state (from controller device or activity)
  isPumpOn: boolean,

  // Running state (from activity observable states)
  isRunning: boolean,
  nextStopLabel: string | null,
  percentComplete: number | null
  pressureKpa: number | null,
  speedFormatted: string | null,
  // Derived metrics
  speedHoursPerRev: number | null,

  timeToStopFormatted: string | null,
  timeToStopMs: number | null
}

/**
 * Data for a CenterPivot with its linked devices and activity.
 */
export interface CenterPivotWithDevices {
  /** Activity data comes from the monitor device */
  activity: DeviceActivity | undefined,
  /** Combined status merging controller and monitor data */
  combinedStatus: CombinedPivotStatus,
  controllerConfig: DeviceConfiguration | undefined,
  monitorConfig: DeviceConfiguration | undefined,
  pivot: CenterPivot,
  /** Primary device for selection: controller if exists, otherwise monitor */
  primaryDeviceId: string | null
}

/**
 * Helper to get the latest pressure reading from activity
 */
function getLatestPressure(activity: DeviceActivity | undefined): number | null {
  if (activity?.pressureReadingKpas == null) {
    return null
  }
  for (let i = activity.pressureReadingKpas.length - 1; i >= 0; i--) {
    const value = activity.pressureReadingKpas[i]
    if (typeof value === "number") {
      return value
    }
  }
  return null
}

/**
 * Compute combined status from controller and monitor data
 */
function computeCombinedStatus(
  controllerConfig: DeviceConfiguration | undefined,
  monitorConfig: DeviceConfiguration | undefined,
  activity: DeviceActivity | undefined,
): CombinedPivotStatus {
  const hasController = Boolean(controllerConfig)
  const hasMonitor = Boolean(monitorConfig)

  // Get activity metrics
  const metrics = getPivotActivityMetrics(activity)

  // Compute direction from metrics
  const direction =
    metrics.direction === "pivot_clockwise"
      ? "clockwise"
      : metrics.direction === "pivot_counterclockwise"
        ? "counterclockwise"
        : null

  // Compute formatted values
  const speedFormatted = formatHoursPerRevolution(metrics.hoursPerRevolution)
  const timeToStopMs = getTimeToNextStopMs(metrics.nextStopExpectedTimestamp)
  const timeToStopFormatted = formatDurationMs(timeToStopMs)

  // Determine running state: pivot is running if it has a direction
  const isRunning = direction !== null

  // Get pressure
  const pressureKpa = getLatestPressure(activity)

  return {
    currentHeadingDegrees: metrics.currentHeadingDegrees,
    direction,
    hasController,
    hasMonitor,
    isEndGunOn: false, // Would need additional data source
    isPumpOn: isRunning, // Assume pump is on when running (could be enhanced)
    isRunning,
    nextStopLabel: metrics.nextStopLabel,
    percentComplete: metrics.percentComplete,
    pressureKpa,
    speedFormatted:
      typeof speedFormatted === "string" ? `${speedFormatted} / rev` : null,
    speedHoursPerRev: metrics.hoursPerRevolution,
    timeToStopFormatted,
    timeToStopMs,
  }
}

/**
 * Get all CenterPivots with their linked device configurations and activity data.
 */
export const getCenterPivotsWithDevices = createSelector(
  [
    selectAllCenterPivots,
    (state: RootState) => Models.deviceConfiguration.selectEntities(state),
    (state: RootState) => Models.deviceActivity.selectEntities(state),
  ],
  (pivots, deviceConfigs, deviceActivities): CenterPivotWithDevices[] => {
    return pivots.map((pivot) => {
      const controllerConfig =
        typeof pivot.controllerDeviceId === "string"
          ? deviceConfigs[pivot.controllerDeviceId]
          : undefined
      const monitorConfig =
        typeof pivot.monitorDeviceId === "string"
          ? deviceConfigs[pivot.monitorDeviceId]
          : undefined
      // Activity data comes from the monitor device (it has the sensors)
      const activity =
        typeof pivot.monitorDeviceId === "string"
          ? deviceActivities[pivot.monitorDeviceId]
          : undefined

      return {
        activity,
        // Compute combined status from all sources
combinedStatus: computeCombinedStatus(
          controllerConfig,
          monitorConfig,
          activity,
        ),
        
controllerConfig,
        
monitorConfig,
        
        
pivot,
        
        // Primary device for selection: prefer controller, fall back to monitor
primaryDeviceId: pivot.controllerDeviceId ?? pivot.monitorDeviceId,
      }
    })
  },
)

/**
 * Get set of device IDs that belong to a CenterPivot entity.
 * Used to filter out individual device markers when rendering pivot markers.
 *
 * Note: Returns a Set for O(1) lookups. The selector is memoized so the Set
 * is only recreated when center pivots change.
 */
export const getDeviceIdsInCenterPivots = createSelector(
  [selectAllCenterPivots],
  (pivots): Set<string> => {
    // Only controllers are subsumed under the pivot's combined marker.
    // Monitors render as standalone device markers so their own position,
    // status, and device card remain visible on the map.
    const ids = new Set<string>()
    for (const pivot of pivots) {
      if (pivot.controllerDeviceId != null) {
        ids.add(pivot.controllerDeviceId)
      }
    }
    return ids
  },
)

/**
 * Get a specific CenterPivotWithDevices by pivot ID.
 * This is more efficient than getting all pivots and filtering.
 */
export function getCenterPivotWithDevicesById(
  state: RootState,
  pivotId: number,
): CenterPivotWithDevices | null {
  const pivot = selectCenterPivotById(state, pivotId)
  if (!pivot) {
    return null
  }

  const deviceConfigs = Models.deviceConfiguration.selectEntities(state)
  const deviceActivities = Models.deviceActivity.selectEntities(state)

  const controllerConfig =
    typeof pivot.controllerDeviceId === "string"
      ? deviceConfigs[pivot.controllerDeviceId]
      : undefined
  const monitorConfig =
    typeof pivot.monitorDeviceId === "string"
      ? deviceConfigs[pivot.monitorDeviceId]
      : undefined
  const activity =
    typeof pivot.monitorDeviceId === "string"
      ? deviceActivities[pivot.monitorDeviceId]
      : undefined

  return {
    activity,
    combinedStatus: computeCombinedStatus(controllerConfig, monitorConfig, activity),
    controllerConfig,
    monitorConfig,
    pivot,
    primaryDeviceId: pivot.controllerDeviceId ?? pivot.monitorDeviceId,
  }
}

/**
 * Item types for grouped device list display.
 *
 * - unified-pivot: A single item representing a CenterPivot entity with all its data
 * - standalone-pivot: A CenterPivot with no linked devices yet
 * - standalone: A regular device not associated with a pivot
 *
 * The legacy "center_pivot" installation type was retired in migration 0264
 * (data) + 0266 (schema); its devices were flipped to `center_pivot_monitor`.
 */
export type GroupedDeviceListItem =
  | { type: "standalone"; device: DeviceStatusData }
  | {
      type: "unified-pivot"
      pivot: CenterPivot
      controllerStatus: DeviceStatusData | undefined
      monitorStatus: DeviceStatusData | undefined
      unifiedData: UnifiedPivotData
    }
  | {
      type: "standalone-pivot"
      pivot: CenterPivot
      unifiedData: UnifiedPivotData
    }

/**
 * Get device list items with unified pivot entries.
 * CenterPivot entities appear as single unified items.
 * Standalone devices and legacy center_pivot devices appear individually.
 */
export const getGroupedDeviceListItems = createSelector(
  [
    getDeviceStatusListItemsSortedFromState,
    getCenterPivotsWithDevices,
    getDeviceIdsInCenterPivots,
    (state: RootState) => state.deviceLastKnownLocations.byDeviceId,
  ],
  (devices, pivotsWithDevices, deviceIdsInPivots, lastKnownByDeviceId): GroupedDeviceListItem[] => {
    const items: GroupedDeviceListItem[] = []

    // Add pivot items (both unified with devices and standalone without)
    pivotsWithDevices.forEach(({ pivot, controllerConfig, monitorConfig, activity }) => {
      // Find device status data for controller and monitor
      const controllerStatus = controllerConfig
        ? devices.find((d) => d.deviceId === controllerConfig.deviceId)
        : undefined
      const monitorStatus = monitorConfig
        ? devices.find((d) => d.deviceId === monitorConfig.deviceId)
        : undefined

      const monitorLastKnown = pivot.monitorDeviceId
        ? lastKnownByDeviceId[pivot.monitorDeviceId]
        : undefined

      // Build unified data for the pivot
      const unifiedData = buildUnifiedPivotData(pivot, controllerConfig, monitorConfig, activity, monitorLastKnown)

      // Add as unified-pivot if at least one device exists, otherwise as standalone-pivot
      if (controllerStatus || monitorStatus) {
        items.push({
          type: "unified-pivot",
          pivot,
          controllerStatus,
          monitorStatus,
          unifiedData,
        })
      } else {
        // Pivot exists but has no linked devices
        items.push({
          type: "standalone-pivot",
          pivot,
          unifiedData,
        })
      }
    })

    // Add standalone devices (not in any pivot). `deviceIdsInPivots` only
    // subsumes controllers — the map intentionally keeps monitors as their
    // own markers. For the *list*, the monitor's status is already shown
    // inside its unified-pivot row, so hide it as a standalone row too.
    const monitorIdsInPivots = new Set<string>()
    for (const { pivot } of pivotsWithDevices) {
      if (typeof pivot.monitorDeviceId === "string") {
        monitorIdsInPivots.add(pivot.monitorDeviceId)
      }
    }
    devices.forEach((device) => {
      if (
        !deviceIdsInPivots.has(device.deviceId) &&
        !monitorIdsInPivots.has(device.deviceId)
      ) {
        items.push({ device, type: "standalone" })
      }
    })

    return items
  },
)

// =============================================================================
// Unified Pivot Data
// =============================================================================

/**
 * Device availability state for a pivot.
 * Indicates which devices (controller/monitor) are available.
 */
export type PivotDeviceState =
  | { type: "full"; controller: DeviceConfiguration; monitor: DeviceConfiguration }
  | { type: "controller-only"; controller: DeviceConfiguration }
  | { type: "monitor-only"; monitor: DeviceConfiguration }
  | { type: "legacy"; device: DeviceConfiguration }
  | { type: "no-devices" }

/**
 * Unified pivot data structure that combines all pivot information
 * from CenterPivot entity, device configurations, and activity data.
 *
 * This is the single source of truth for pivot display and control.
 */
export interface UnifiedPivotData {
  // === Identity ===
  /** CenterPivot entity ID (null for legacy center_pivot devices) */
  pivotId: number | null
  /** Display name for the pivot */
  name: string

  // === Geographic Configuration ===
  /** Center point of the pivot in lat/lng */
  pivotCenter: Geo.RnmLatLng
  /** Radius of the pivot span in meters */
  pivotRadiusMeters: number
  /** Boundary start angle in degrees (0 = North) */
  boundaryStartAngle: number
  /** Boundary end angle in degrees */
  boundaryEndAngle: number
  /** End gun throw radius in meters */
  endGunThrowRadiusMeters: number
  /** Whether the pivot covers a full 360-degree circle (no boundary limits) */
  isFullCircle: boolean

  // === Devices ===
  /** Controller device ID (sends commands) */
  controllerDeviceId: string | null
  /** Monitor device ID (reports sensor data) */
  monitorDeviceId: string | null
  /** Primary device ID for selection (controller preferred) */
  primaryDeviceId: string | null
  /** Device availability state */
  deviceState: PivotDeviceState

  // === Capabilities ===
  /** Whether this pivot supports direction control */
  hasDirectionControl: boolean
  /** Whether this pivot supports speed control */
  hasSpeedControl: boolean
  /** Whether this pivot has an end gun */
  hasEndGun: boolean
  /** Whether this pivot supports wet/dry control */
  hasWetDryControl: boolean

  // === Speed-by-Depth Configuration ===
  /** Average flow rate in liters per minute (for depth-based speed calculation) */
  averageFlowRateLpm: number | null
  /** Tip linear speed at the controller's maximum (100%) setting, in meters/hour. */
  maxSpeedMetersPerHour: number | null
  /** Tip linear speed at the slowest non-zero controller setting, in meters/hour. */
  minSpeedMetersPerHour: number | null
  /** Calibrated starting controller percent for closed-loop speed control. */
  initialSpeedPercent: number | null
  /** Whether the pivot has all configuration needed for speed-by-depth calculation */
  hasSpeedByDepthConfig: boolean

  // === Activity Metrics ===
  /** Raw activity metrics from monitor device */
  metrics: PivotActivityMetrics
  /** Sector drawing values for activity visualization */
  activitySector: PivotActivityStartAndEndValues | null
  /** Whether we have valid activity data */
  hasActivityData: boolean

  // === Derived State ===
  /** Whether the pivot is currently moving */
  isMoving: boolean
  /** Whether moving clockwise (true) or counter-clockwise (false) */
  isClockwise: boolean
  /** Current heading in degrees (0-360) */
  currentAngleDegrees: number
  /**
   * True when currentAngleDegrees came from a real source — either activity
   * metrics or a GPS-derived pivot heading from the monitor's last-known
   * location. False when only a configured default is available, in which
   * case the marker should suppress span line / end gun / direction rendering.
   */
  hasResolvedAngle: boolean
  /** Speed as percentage (0-100) */
  speedPercentage: number

  // === Formatted Display Values ===
  formatted: FormattedPivotMetrics
}

// Default values
const DEFAULT_PIVOT_CENTER: Geo.RnmLatLng = Geo.PointsOfInterest.CENTER_OF_USA.native
const DEFAULT_PIVOT_RADIUS = 400
const DEFAULT_BOUNDARY_START = 0
const DEFAULT_BOUNDARY_END = 360
const DEFAULT_END_GUN_THROW = 40
const DEFAULT_SPEED_PERCENT = 50

/**
 * Get the device state for a pivot based on available configurations.
 */
function getDeviceState(
  controllerConfig: DeviceConfiguration | undefined,
  monitorConfig: DeviceConfiguration | undefined,
  legacyConfig?: DeviceConfiguration,
): PivotDeviceState {
  if (controllerConfig && monitorConfig) {
    return { type: "full", controller: controllerConfig, monitor: monitorConfig }
  }
  if (controllerConfig) {
    return { type: "controller-only", controller: controllerConfig }
  }
  if (monitorConfig) {
    return { type: "monitor-only", monitor: monitorConfig }
  }
  if (legacyConfig) {
    return { type: "legacy", device: legacyConfig }
  }
  return { type: "no-devices" }
}

/**
 * Build unified pivot data from a CenterPivot entity and its linked devices.
 */
function buildUnifiedPivotData(
  pivot: CenterPivot,
  controllerConfig: DeviceConfiguration | undefined,
  monitorConfig: DeviceConfiguration | undefined,
  activity: DeviceActivity | undefined,
  monitorLastKnown: DeviceLastKnownLocation | undefined,
): UnifiedPivotData {
  // Get activity metrics
  const metrics = getPivotActivityMetrics(activity ?? null)
  // Suppress the swept-sector visual once the activity is finished — its
  // trailing pivotHeadingsDegrees/travelerDirections arrays would otherwise
  // keep drawing a stale arc.
  const activitySector =
    activity && !isActivityFinished(activity)
      ? getSectorDrawingValuesForPivotActivity(activity) ?? null
      : null
  const hasActivityData = activitySector !== null

  // Derive state
  const isMoving = isPivotMoving(metrics)
  const isClockwise = isPivotClockwise(metrics)
  // Angle resolution order:
  //   1. Activity's calculated heading (most current during a run).
  //   2. Backend-fitted pivot heading from the monitor's last-known GPS
  //      (lets the marker show the correct arm position while idle).
  //   3. Final heading of the last (finished) run — the arm doesn't move
  //      when it isn't running, so this is still the real position even
  //      when the monitor has gone stale.
  //   4. First configured boundary stop or 0° — purely a placeholder so
  //      geometry computations don't blow up. hasResolvedAngle is false here.
  const resolvedAngle =
    metrics.currentHeadingDegrees ??
    monitorLastKnown?.pivotHeadingDegrees ??
    getLastKnownPivotHeading(activity)
  const currentAngleDegrees =
    resolvedAngle ??
    pivot.boundary?.startDegrees ??
    DEFAULT_BOUNDARY_START
  const hasResolvedAngle = resolvedAngle !== null
  const speedPercentage = hoursPerRevolutionToSpeedPercent(metrics.hoursPerRevolution) ?? DEFAULT_SPEED_PERCENT

  // Get geographic configuration from pivot entity
  const pivotCenter: Geo.RnmLatLng = pivot.pivotCenterGpsLocation
    ? Geo.point(pivot.pivotCenterGpsLocation)?.toNative() ?? DEFAULT_PIVOT_CENTER
    : DEFAULT_PIVOT_CENTER

  // boundary === null = full circle. The Boundary union type guarantees
  // both endpoints are present together when the field is non-null.
  const isFullCircle = pivot.boundary == null
  const boundaryStartAngle = pivot.boundary?.startDegrees ?? DEFAULT_BOUNDARY_START
  const boundaryEndAngle = pivot.boundary?.endDegrees ?? DEFAULT_BOUNDARY_END

  return {
    // Identity
    pivotId: pivot.id,
    name: pivot.name,

    // Geographic
    pivotCenter,
    pivotRadiusMeters: pivot.pivotRadiusMeters ?? DEFAULT_PIVOT_RADIUS,
    boundaryStartAngle,
    boundaryEndAngle,
    endGunThrowRadiusMeters: pivot.endGunThrowRadiusMeters ?? DEFAULT_END_GUN_THROW,
    isFullCircle,

    // Devices
    controllerDeviceId: pivot.controllerDeviceId,
    monitorDeviceId: pivot.monitorDeviceId,
    primaryDeviceId: pivot.controllerDeviceId ?? pivot.monitorDeviceId,
    deviceState: getDeviceState(controllerConfig, monitorConfig),

    // Capabilities
    hasDirectionControl: pivot.hasDirectionControl,
    hasSpeedControl: pivot.hasSpeedControl,
    hasEndGun: pivot.hasEndGun,
    hasWetDryControl: pivot.hasWetDryControl,

    // Speed-by-depth configuration
    averageFlowRateLpm: pivot.averageFlowRateLpm ?? null,
    maxSpeedMetersPerHour: pivot.maxSpeedMetersPerHour ?? null,
    minSpeedMetersPerHour: pivot.minSpeedMetersPerHour ?? null,
    initialSpeedPercent: pivot.initialSpeedPercent ?? null,
    hasSpeedByDepthConfig: checkHasSpeedByDepthConfig({
      averageFlowRateLpm: pivot.averageFlowRateLpm,
      pivotRadiusMeters: pivot.pivotRadiusMeters,
      maxSpeedMetersPerHour: pivot.maxSpeedMetersPerHour,
    }),

    // Activity
    metrics,
    activitySector,
    hasActivityData,

    // Derived state
    isMoving,
    isClockwise,
    currentAngleDegrees,
    hasResolvedAngle,
    speedPercentage,

    // Formatted — if activity didn't supply a heading but we resolved one
    // from the monitor's last-known GPS, surface it so the UI still shows
    // the pivot's angle while idle.
    formatted: withFallbackHeading(getFormattedPivotMetrics(metrics), resolvedAngle),
  }
}

function withFallbackHeading(
  formatted: FormattedPivotMetrics,
  fallbackAngleDegrees: number | null,
): FormattedPivotMetrics {
  if (formatted.heading !== null || fallbackAngleDegrees === null) {
    return formatted
  }
  return { ...formatted, heading: formatPivotHeading(fallbackAngleDegrees) }
}

/**
 * Build unified pivot data from a legacy center_pivot device configuration.
 * Used when a device has installation type "center_pivot" without a CenterPivot entity.
 */
function buildUnifiedPivotDataFromLegacyDevice(
  config: DeviceConfiguration,
  activity: DeviceActivity | undefined,
  lastKnown: DeviceLastKnownLocation | undefined,
): UnifiedPivotData {
  // Get activity metrics
  const metrics = getPivotActivityMetrics(activity ?? null)
  const activitySector =
    activity && !isActivityFinished(activity)
      ? getSectorDrawingValuesForPivotActivity(activity) ?? null
      : null
  const hasActivityData = activitySector !== null

  // Derive state. The legacy `center_pivot` installation type was retired
  // in backend migration 0264; any device still hitting this branch has no
  // boundary information on the device-config side, so treat it as a full
  // circle with default angles.
  const isMoving = isPivotMoving(metrics)
  const isClockwise = isPivotClockwise(metrics)
  const isFullCircle = true
  const boundaryStartAngle = DEFAULT_BOUNDARY_START
  const resolvedAngle =
    metrics.currentHeadingDegrees ??
    lastKnown?.pivotHeadingDegrees ??
    getLastKnownPivotHeading(activity)
  const currentAngleDegrees = resolvedAngle ?? boundaryStartAngle
  const hasResolvedAngle = resolvedAngle !== null
  const speedPercentage = hoursPerRevolutionToSpeedPercent(metrics.hoursPerRevolution) ?? DEFAULT_SPEED_PERCENT

  // Get geographic configuration from device config
  const pivotCenter: Geo.RnmLatLng = config.pivotCenterGpsLocation
    ? Geo.point(config.pivotCenterGpsLocation)?.toNative() ?? DEFAULT_PIVOT_CENTER
    : DEFAULT_PIVOT_CENTER

  const boundaryEndAngle = DEFAULT_BOUNDARY_END

  return {
    // Identity
    pivotId: null,
    name: config.deviceName ?? config.codaDeviceAlias,

    // Geographic
    pivotCenter,
    pivotRadiusMeters: config.pivotRadiusMeters ?? DEFAULT_PIVOT_RADIUS,
    boundaryStartAngle,
    boundaryEndAngle,
    endGunThrowRadiusMeters: DEFAULT_END_GUN_THROW,
    isFullCircle,

    // Devices - legacy device acts as both controller and monitor
    controllerDeviceId: config.deviceId,
    monitorDeviceId: config.deviceId,
    primaryDeviceId: config.deviceId,
    deviceState: getDeviceState(undefined, undefined, config),

    // Capabilities - legacy devices have all capabilities
    hasDirectionControl: true,
    hasSpeedControl: true,
    hasEndGun: true,
    hasWetDryControl: true,

    // Speed-by-depth configuration - not supported for legacy devices
    averageFlowRateLpm: null,
    maxSpeedMetersPerHour: null,
    minSpeedMetersPerHour: null,
    initialSpeedPercent: null,
    hasSpeedByDepthConfig: false,

    // Activity
    metrics,
    activitySector,
    hasActivityData,

    // Derived state
    isMoving,
    isClockwise,
    currentAngleDegrees,
    hasResolvedAngle,
    speedPercentage,

    // Formatted — see buildUnifiedPivotData for the fallback rationale.
    formatted: withFallbackHeading(getFormattedPivotMetrics(metrics), resolvedAngle),
  }
}

/**
 * Get unified pivot data for a specific device.
 * Works for both new-style (controller/monitor) and legacy (center_pivot) devices.
 * Memoized by deviceId to prevent unnecessary recalculations.
 *
 * @param state - Redux root state
 * @param deviceId - Device ID to get pivot data for
 * @returns UnifiedPivotData or null if not a pivot device
 */
export const getUnifiedPivotDataForDevice = createCachedSelector(
  [
    (state: RootState, deviceId: string) =>
      Models.deviceConfiguration.selectById(state, deviceId),
    selectAllCenterPivots,
    (state: RootState) => Models.deviceConfiguration.selectEntities(state),
    (state: RootState) => Models.deviceActivity.selectEntities(state),
    (state: RootState) => state.deviceLastKnownLocations.byDeviceId,
  ],
  (config, pivots, deviceConfigs, deviceActivities, lastKnownByDeviceId): UnifiedPivotData | null => {
    if (!config) return null

    const installationType = config.deviceInstallationType

    // Check if this is a new-style pivot device linked to a CenterPivot entity
    if (
      (installationType === "center_pivot_controller" ||
        installationType === "center_pivot_monitor") &&
      config.centerPivotId
    ) {
      const pivot = pivots.find((p) => p.id === config.centerPivotId)
      if (!pivot) return null

      const controllerConfig = pivot.controllerDeviceId
        ? deviceConfigs[pivot.controllerDeviceId]
        : undefined
      const monitorConfig = pivot.monitorDeviceId
        ? deviceConfigs[pivot.monitorDeviceId]
        : undefined
      const activity = pivot.monitorDeviceId
        ? deviceActivities[pivot.monitorDeviceId]
        : undefined
      const monitorLastKnown = pivot.monitorDeviceId
        ? lastKnownByDeviceId[pivot.monitorDeviceId]
        : undefined

      return buildUnifiedPivotData(pivot, controllerConfig, monitorConfig, activity, monitorLastKnown)
    }

    // Check if this is a legacy center_pivot device
    if (installationType === "center_pivot") {
      const activity = deviceActivities[config.deviceId]
      const lastKnown = lastKnownByDeviceId[config.deviceId]
      return buildUnifiedPivotDataFromLegacyDevice(config, activity, lastKnown)
    }

    return null
  },
)(
  // Cache key resolver - memoize by deviceId
  (_state, deviceId) => deviceId,
)

/**
 * Get unified pivot data for a CenterPivot entity by ID.
 * Memoized by pivotId to prevent unnecessary recalculations.
 *
 * @param state - Redux root state
 * @param pivotId - CenterPivot entity ID
 * @returns UnifiedPivotData or null if pivot not found
 */
export const getUnifiedPivotDataById = createCachedSelector(
  [
    (state: RootState, pivotId: number) => selectCenterPivotById(state, pivotId),
    (state: RootState) => Models.deviceConfiguration.selectEntities(state),
    (state: RootState) => Models.deviceActivity.selectEntities(state),
    (state: RootState) => state.deviceLastKnownLocations.byDeviceId,
  ],
  (pivot, deviceConfigs, deviceActivities, lastKnownByDeviceId): UnifiedPivotData | null => {
    if (!pivot) return null

    const controllerConfig = pivot.controllerDeviceId
      ? deviceConfigs[pivot.controllerDeviceId]
      : undefined
    const monitorConfig = pivot.monitorDeviceId
      ? deviceConfigs[pivot.monitorDeviceId]
      : undefined
    const activity = pivot.monitorDeviceId
      ? deviceActivities[pivot.monitorDeviceId]
      : undefined
    const monitorLastKnown = pivot.monitorDeviceId
      ? lastKnownByDeviceId[pivot.monitorDeviceId]
      : undefined

    return buildUnifiedPivotData(pivot, controllerConfig, monitorConfig, activity, monitorLastKnown)
  },
)(
  // Cache key resolver - memoize by pivotId
  (_state, pivotId) => pivotId,
)

/**
 * Get a map of center pivot action names to their NamedDeviceAction IDs.
 * Returns Record<string, number> for all actions whose name starts with "cp_controller_".
 */
export const getCenterPivotActionsByName = createSelector(
  [(state: RootState) => Models.namedDeviceAction.selectAll(state)],
  (actions): Record<string, number> => {
    const result: Record<string, number> = {}
    for (const action of actions) {
      if (action.name != null && action.name.startsWith("cp_controller_")) {
        result[action.name] = action.id
      }
    }
    return result
  },
)

/**
 * Get the CP (center pivot controller) SensorEventLast entry for a device.
 * Returns the entry with sensorType === "CP", or undefined if none exists.
 */
export const getCpSensorEventLastForDevice = createCachedSelector(
  [
    (state: RootState, deviceId: string) =>
      getSensorEventLastsForDevice(state, deviceId),
  ],
  (sensorEventLasts) => {
    return sensorEventLasts.find((entry) => entry.sensorType === "CP") ?? null
  },
)(
  (_state, deviceId) => deviceId,
)

/**
 * Get the sensor ID for the CP sensor on a device.
 * Used for pending action tracking.
 */
export const getCpSensorIdForDevice = createCachedSelector(
  [
    (state: RootState, deviceId: string) =>
      getCpSensorEventLastForDevice(state, deviceId),
  ],
  (cpEntry) => {
    return cpEntry?.sensorId ?? null
  },
)(
  (_state, deviceId) => deviceId,
)

/**
 * Get the unified wet/dry status for a pivot: the controller's CP wet field
 * when a controller is linked, otherwise the monitor's pressure state as a
 * proxy (pressurized line = wet). Pressure lives on the monitor's last
 * DeviceEvent, not on SensorEventLastEntry, hence the deviceEventLast input.
 */
export const getPivotWetStatusById = createCachedSelector(
  [
    (state: RootState, pivotId: number) => selectCenterPivotById(state, pivotId),
    (state: RootState) => state.deviceEventLast.sensorEventLasts,
    (state: RootState) => Models.deviceEventLast.selectEntities(state),
  ],
  (pivot, sensorEventLastsByDeviceId, eventLastById): PivotWetStatus => {
    const cpEntry = pivot?.controllerDeviceId != null
      ? (sensorEventLastsByDeviceId[pivot.controllerDeviceId] ?? []).find(
          (entry) => entry.sensorType === "CP",
        )
      : undefined
    const monitorEvent = pivot?.monitorDeviceId != null
      ? eventLastById[pivot.monitorDeviceId]
      : undefined
    return derivePivotWetStatus({
      cpWet: cpEntry?.centerPivotWet,
      pressureState:
        monitorEvent?.pressure?.stateCurrent ??
        monitorEvent?.pressureSwitch?.stateCurrent,
    })
  },
)(
  (_state, pivotId) => pivotId,
)

/**
 * Get all unified pivot data for the current farm.
 * Includes both CenterPivot entities and legacy center_pivot devices.
 */
export const getAllUnifiedPivotData = createSelector(
  [
    selectAllCenterPivots,
    (state: RootState) => Models.deviceConfiguration.selectEntities(state),
    (state: RootState) => Models.deviceConfiguration.selectAll(state),
    (state: RootState) => Models.deviceActivity.selectEntities(state),
    (state: RootState) => state.deviceLastKnownLocations.byDeviceId,
  ],
  (pivots, deviceConfigs, allDeviceConfigs, deviceActivities, lastKnownByDeviceId): UnifiedPivotData[] => {
    const result: UnifiedPivotData[] = []
    const processedDeviceIds = new Set<string>()

    // Process CenterPivot entities
    for (const pivot of pivots) {
      const controllerConfig = pivot.controllerDeviceId
        ? deviceConfigs[pivot.controllerDeviceId]
        : undefined
      const monitorConfig = pivot.monitorDeviceId
        ? deviceConfigs[pivot.monitorDeviceId]
        : undefined
      const activity = pivot.monitorDeviceId
        ? deviceActivities[pivot.monitorDeviceId]
        : undefined
      const monitorLastKnown = pivot.monitorDeviceId
        ? lastKnownByDeviceId[pivot.monitorDeviceId]
        : undefined

      result.push(buildUnifiedPivotData(pivot, controllerConfig, monitorConfig, activity, monitorLastKnown))

      // Track processed device IDs
      if (pivot.controllerDeviceId) processedDeviceIds.add(pivot.controllerDeviceId)
      if (pivot.monitorDeviceId) processedDeviceIds.add(pivot.monitorDeviceId)
    }

    // Process legacy center_pivot devices not linked to a CenterPivot entity
    for (const config of allDeviceConfigs) {
      if (
        config.deviceInstallationType === "center_pivot" &&
        !config.centerPivotId &&
        !processedDeviceIds.has(config.deviceId)
      ) {
        const activity = deviceActivities[config.deviceId]
        const lastKnown = lastKnownByDeviceId[config.deviceId]
        result.push(buildUnifiedPivotDataFromLegacyDevice(config, activity, lastKnown))
      }
    }

    return result
  },
)
